// Secure token service - this should be implemented on your backend/server-side
// DO NOT implement this logic in the frontend in production

import { OktaAuth } from '@okta/okta-auth-js';

interface ApiTokens {
  clientId: string;
  clientSecret: string;
  expiresAt: number;
}

interface TokenCache {
  [userId: string]: ApiTokens;
}

class SecureTokenService {
  private tokenCache: TokenCache = {};
  private readonly TOKEN_EXPIRY_MS = 3600000; // 1 hour

  /**
   * Get API tokens for authenticated user
   * This should be called from your backend service, not frontend
   */
  async getApiTokensForUser(oktaUserId: string, oktaToken: string): Promise<ApiTokens | null> {
    try {
      // Check cache first
      const cachedToken = this.tokenCache[oktaUserId];
      if (cachedToken && cachedToken.expiresAt > Date.now()) {
        return cachedToken;
      }

      // In production, this would call your OAuth2 token endpoint
      // with proper server-side credentials
      const response = await fetch(process.env.VITE_EXPERIAN_TOKEN_ENDPOINT || '', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Authorization': `Bearer ${oktaToken}`, // Use Okta token for additional security
        },
        body: new URLSearchParams({
          'grant_type': 'client_credentials',
          'client_id': process.env.API_CLIENT_ID || '',
          'client_secret': process.env.API_CLIENT_SECRET || '',
          'scope': 'api.read api.write'
        })
      });

      if (!response.ok) {
        throw new Error(`Token request failed: ${response.statusText}`);
      }

      const tokenData = await response.json();
      
      const tokens: ApiTokens = {
        clientId: tokenData.client_id,
        clientSecret: tokenData.access_token, // Use access token as client secret
        expiresAt: Date.now() + this.TOKEN_EXPIRY_MS
      };

      // Cache the token
      this.tokenCache[oktaUserId] = tokens;
      
      return tokens;

    } catch (error) {
      console.error('Error fetching API tokens:', error);
      return null;
    }
  }

  /**
   * Clear cached tokens for a user (e.g., on logout)
   */
  clearTokensForUser(oktaUserId: string): void {
    delete this.tokenCache[oktaUserId];
  }

  /**
   * Clear all cached tokens
   */
  clearAllTokens(): void {
    this.tokenCache = {};
  }
}

export const secureTokenService = new SecureTokenService();

// Helper function to get tokens securely in the frontend
export const getSecureApiCredentials = async (oktaAuth: OktaAuth, mode?: string): Promise<{issued_at: string,expires_in:string,token_type:string,refresh_token: string, access_token: string} | null> => {
  try {
    const user = await oktaAuth.getUser();
    const accessToken = await oktaAuth.getAccessToken();
    if (!user?.sub || !accessToken) {
      console.error('User not authenticated or missing tokens');
      return null;
    }

   // REST API call to your backend to get secure API credentials.
    const response = await fetch('https://7spo0l794h.execute-api.us-east-1.amazonaws.com/Deploy-MPS1-tokenAPI/dev_mPS1_token_creator', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'test-type': mode === 'prod' ? 'sanity' : 'regression',
        'Authorization': `Bearer ${accessToken}`,
      }
    });
   
    if (response.ok) {
      const tokens = await response.json();
      return {
        issued_at : tokens.issued_at,
        expires_in: tokens.expires_in,
        token_type: tokens.token_type,
        access_token: tokens.access_token,
        refresh_token: tokens.refresh_token 
      };
    }

    throw new Error('Failed to get API credentials from backend');
    
  } catch (error) {
    console.error('Error getting secure API credentials:', error);
    return null;
  }
};